import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import AlertDialogContext from "./AlertDialogContext";
import useAlertDialog from "./useAlertDialog";

export const AlertDialogButtonType = Object.freeze({
  PRIMARY: "primary",
  DESTRUCTIVE: "destructive",
  SIMPLE: "simple",
});

const buttonClasses = {
  [AlertDialogButtonType.PRIMARY]:
    "rounded bg-primary px-6 py-2 text-sm font-bold uppercase text-white shadow transition-all duration-150 hover:bg-primary-dark hover:shadow-lg focus:outline-none disabled:cursor-not-allowed disabled:opacity-60",
  [AlertDialogButtonType.DESTRUCTIVE]:
    "rounded px-6 py-2 text-sm font-bold uppercase text-red-500 transition-all duration-150 hover:bg-red-50 hover:text-icon-red focus:outline-none disabled:cursor-not-allowed disabled:opacity-60",
  [AlertDialogButtonType.SIMPLE]:
    "rounded px-6 py-2 text-sm font-bold uppercase text-slate-600 transition-all duration-150 hover:bg-slate-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60",
};

const withActionDefaults = (action, defaults) => ({
  ...defaults,
  ...action,
});

function AlertDialogButton({ action, disabled, onAction }) {
  const type = buttonClasses[action.type]
    ? action.type
    : AlertDialogButtonType.SIMPLE;

  return (
    <button
      type="button"
      disabled={disabled}
      className={buttonClasses[type]}
      onClick={() => onAction(action)}
    >
      {action.label}
    </button>
  );
}

function AlertDialog({ dialog, onClose }) {
  const [actionPending, setActionPending] = useState(false);
  const dialogRef = useRef(null);
  const previouslyFocusedElement = useRef(null);
  const titleId = useId();

  const dismiss = useCallback(() => {
    if (actionPending) return;
    dialog.onDismiss?.();
    onClose();
  }, [actionPending, dialog, onClose]);

  useEffect(() => {
    previouslyFocusedElement.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") dismiss();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement.current?.focus?.();
    };
  }, [dismiss]);

  const handleAction = async (action) => {
    setActionPending(true);

    try {
      await action.onClick?.();
      onClose();
    } finally {
      setActionPending(false);
    }
  };

  return createPortal(
    <div
      data-testid="alert-dialog-backdrop"
      className="fixed inset-0 z-[100] flex min-h-screen w-full items-center justify-center bg-black/70 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) dismiss();
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={dialog.title ? titleId : undefined}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-md bg-white p-6 shadow-xl"
      >
        {dialog.title ? (
          <h2 id={titleId} className="text-lg font-semibold text-slate-900">
            {dialog.title}
          </h2>
        ) : null}

        <div className={dialog.title ? "mt-5" : ""}>{dialog.children}</div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          {dialog.actions.map((action) => (
            <AlertDialogButton
              key={action.key}
              action={action}
              disabled={actionPending}
              onAction={handleAction}
            />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function AlertDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);

  const close = useCallback(() => setDialog(null), []);

  const alert = useCallback(({ title, children, action, onDismiss } = {}) => {
    setDialog({
      title,
      children,
      onDismiss,
      actions: [
        {
          key: "alert",
          ...withActionDefaults(action, {
            label: "OK",
            type: AlertDialogButtonType.PRIMARY,
          }),
        },
      ],
    });
  }, []);

  const confirm = useCallback(
    ({ title, children, confirmAction, cancelAction, onDismiss } = {}) => {
      setDialog({
        title,
        children,
        onDismiss,
        actions: [
          {
            key: "cancel",
            ...withActionDefaults(cancelAction, {
              label: "Cancel",
              type: AlertDialogButtonType.SIMPLE,
            }),
          },
          {
            key: "confirm",
            ...withActionDefaults(confirmAction, {
              label: "Confirm",
              type: AlertDialogButtonType.PRIMARY,
            }),
          },
        ],
      });
    },
    [],
  );

  const value = useMemo(
    () => ({ alert, close, confirm }),
    [alert, close, confirm],
  );

  return (
    <AlertDialogContext.Provider value={value}>
      {children}
      {dialog ? <AlertDialog dialog={dialog} onClose={close} /> : null}
    </AlertDialogContext.Provider>
  );
}

export { useAlertDialog };
